import React, { useEffect, useState } from "react";

// assets
import { ICONS } from "../../../../assets/icons";

// utils
import { Popover } from "antd";

// hooks
import useToggle from "../../../../hooks/useToggle";

// api
import { getScopesAndAttributes } from "../../api/Api";

/**
 * Converts attribute name to hyphenated format
 * Replaces underscores and spaces with hyphens, converts to lowercase
 * @param {string} name - Attribute name (e.g., "first_name", "preferred location 1")
 * @returns {string} - Hyphenated name (e.g., "first-name", "preferred-location-1")
 */
const toHyphenatedName = (name) => {
  if (!name) return "";
  return name.toLowerCase().replace(/[_\s]+/g, "-");
};

/**
 * Extracts variable names from template text
 * @param {string} text - Template text containing variables like {{variable-name}}
 * @returns {string[]} - Array of variable names (without braces)
 */
const getUsedVariables = (text) => {
  if (!text) return [];
  const matches = text.match(/\{\{([a-z0-9_\-\s]+)\}\}/gi) || [];
  return matches.map((m) => toHyphenatedName(m.replace(/\{\{|\}\}/g, "")));
};

/**
 * Check if variable name is a custom variable (custom-variable1, custom-variable2, etc.)
 * @param {string} name - Variable name
 * @returns {boolean}
 */
const isCustomVariable = (name) => /^custom-variable\d+$/i.test(name);

/**
 * Get the next custom variable number
 * @param {string[]} usedVariables - Array of used variable names
 * @returns {number}
 */
const getNextCustomVariableNumber = (usedVariables) => {
  const customVars = usedVariables.filter((v) => isCustomVariable(v));
  if (customVars.length === 0) return 1;

  const numbers = customVars.map((v) => {
    const match = v.match(/custom-variable(\d+)/i);
    return match ? parseInt(match[1], 10) : 0;
  });

  return Math.max(...numbers) + 1;
};

export default function AddVariable({
  disable,
  onChange,
  allow,
  brandId,
  text = "",
}) {
  const [isOpen, setIsOpen] = useToggle(false);
  const [scopes, setScopes] = useState([]);
  const [selectedScope, setSelectedScope] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get used variables from text
  const usedVariables = getUsedVariables(text);

  // Fetch scopes and attributes on mount
  useEffect(() => {
    if (brandId && isOpen && scopes.length === 0) {
      fetchScopesAndAttributes();
    }
  }, [brandId, isOpen]);

  const fetchScopesAndAttributes = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getScopesAndAttributes(brandId);
      if (response?.data?.scopes_and_attributes_list) {
        setScopes(response.data.scopes_and_attributes_list);
      }
    } catch (err) {
      console.error("Error fetching scopes and attributes:", err);
      setError("Failed to load attributes");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle scope click - show attributes
  const handleScopeClick = (scope) => {
    setSelectedScope(scope);
  };

  // Handle back button - go back to scopes list
  const handleBack = () => {
    setSelectedScope(null);
  };

  // Handle attribute selection
  const handleAttributeSelect = (attribute) => {
    // Convert attribute name to hyphenated format
    const hyphenatedName = toHyphenatedName(attribute.name);
    if (onChange) {
      onChange({
        title: hyphenatedName,
        id: attribute.id,
        label: attribute.label,
        type: "profile_attribute",
        attributeType: attribute.attribute_type,
        originalName: attribute.name, // Keep original for backend reference
      });
    }
    setIsOpen(false);
    setSelectedScope(null);
  };

  // Handle custom variable selection
  const handleCustomVariableSelect = () => {
    const nextNumber = getNextCustomVariableNumber(usedVariables);
    if (onChange) {
      onChange({
        title: `custom-variable${nextNumber}`,
        id: `custom-${nextNumber}`,
        type: "custom",
      });
    }
    setIsOpen(false);
    setSelectedScope(null);
  };

  // Check if attribute is already used (compare using hyphenated format)
  const isAttributeUsed = (attributeName) => {
    const hyphenatedName = toHyphenatedName(attributeName);
    return usedVariables.includes(hyphenatedName);
  };

  // Render loading state
  const renderLoading = () => (
    <div className="flex items-center justify-center py-[1vw]">
      <div className="w-[1vw] h-[1vw] border-2 border-[var(--new-primary)] border-t-transparent rounded-full animate-spin"></div>
      <span className="ml-[0.5vw] font-md text-[var(--font-400)]">
        Loading...
      </span>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className="px-[0.6vw] py-[0.5vw] font-md text-red-500">{error}</div>
  );

  // Render scopes list (Level 1)
  const renderScopesList = () => (
    <div className="min-w-[14vw]">
      {scopes.map((scope) => {
        // Only show scopes that have profile_attributes
        if (!scope.profile_attributes || scope.profile_attributes.length === 0)
          return null;

        return (
          <div
            key={scope.id}
            className="flex items-center justify-between px-[0.6vw] py-[0.5vw] cursor-pointer rounded-[0.15vw] hover:bg-slate-100 border-b-[0.08vw] border-[var(--border-50)] last:border-b-0"
            onClick={() => handleScopeClick(scope)}
          >
            <span className="font-md weight-medium text-[var(--contentText)]">
              {scope.label}
            </span>
            <img
              src={ICONS?.chevronRight || ICONS?.SelectDropdown}
              alt=">"
              className="w-[0.8vw] h-[0.8vw] rotate-0"
            />
          </div>
        );
      })}

      {/* Divider */}
      {/* <div className="border-t-[0.08vw] border-[var(--border-50)] my-[0.3vw]"></div> */}

      {/* Custom Variable Option */}
      <div
        className="flex items-center gap-[0.4vw] px-[0.6vw] py-[0.5vw] cursor-pointer rounded-[0.15vw] hover:bg-slate-100"
        onClick={handleCustomVariableSelect}
      >
        <img
          src={ICONS?.addBGwhite}
          alt="+"
          className="w-[0.8vw] h-[0.8vw] opacity-60"
          style={{ filter: "invert(1)" }}
        />
        <span className="font-md weight-medium text-[var(--contentText)]">
          Custom Variable
        </span>
      </div>
    </div>
  );

  // Render attributes list (Level 2)
  const renderAttributesList = () => (
    <div className="min-w-[14vw]">
      {/* Back button with scope name */}
      <div
        className="flex items-center gap-[0.4vw] px-[0.6vw] py-[0.5vw] cursor-pointer rounded-[0.15vw] hover:bg-slate-100 border-b-[0.08vw] border-[var(--border-50)] bg-slate-50"
        onClick={handleBack}
      >
        <img
          src={ICONS?.chevronRight || ICONS?.SelectDropdown}
          alt="<"
          className="w-[0.8vw] h-[0.8vw] rotate-180"
        />
        <span className="font-md weight-semibold text-[var(--contentText)]">
          {selectedScope.label}
        </span>
      </div>

      {/* Attributes list */}
      {selectedScope.profile_attributes?.map((attr) => {
        const isUsed = isAttributeUsed(attr.name);

        return (
          <div
            key={attr.id}
            className={`flex items-center gap-[0.4vw] px-[0.6vw] py-[0.5vw] rounded-[0.15vw] border-b-[0.08vw] border-[var(--border-50)] last:border-b-0 ${
              isUsed
                ? "bg-slate-50 cursor-not-allowed opacity-50"
                : "cursor-pointer hover:bg-slate-100"
            }`}
            onClick={() => {
              if (!isUsed) handleAttributeSelect(attr);
            }}
            title={isUsed ? "Already used in template" : `Add {{${toHyphenatedName(attr.name)}}}`}
          >
            <img
              src={ICONS?.addBGwhite}
              alt="+"
              className="w-[0.8vw] h-[0.8vw] opacity-60"
              style={{ filter: "invert(1)" }}
            />
            <span
              className={`font-md weight-medium ${
                isUsed ? "text-[var(--font-400)]" : "text-[var(--contentText)]"
              }`}
            >
              {attr.label}
            </span>
            {isUsed && (
              <span className="ml-[0.5vw] font-xs text-[var(--font-400)]">
                (used)
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  // Render dropdown content
  const renderContent = () => {
    if (isLoading) return renderLoading();
    if (error) return renderError();
    if (selectedScope) return renderAttributesList();
    return renderScopesList();
  };

  return (
    <Popover
      trigger={allow && !disable ? ["click"] : []}
      open={isOpen}
      arrow={false}
      onOpenChange={(value) => {
        if (allow && !disable) {
          setIsOpen(value);
          if (!value) {
            // Reset to scopes view when closing
            setSelectedScope(null);
          }
        }
      }}
      content={<div className="px-[0.3vw] py-[0.3vw]">{renderContent()}</div>}
      placement="bottomRight"
    >
      <div
        className={`flex items-center gap-[0.5vw] font-md weight-semibold px-[0.5vw] py-[0.5vw] rounded-[0.3vw] ${
          !allow || (allow && disable)
            ? "bg-[var(--lightGrey)] text-[var(--darkgrey)] cursor-not-allowed"
            : "bg-[var(--new-primary)] text-[var(--white)] cursor-pointer"
        }`}
      >
        <img src={ICONS?.addBGwhite} alt="add" /> Add Variable
      </div>
    </Popover>
  );
}

// Keep the old export for backward compatibility during transition
export const addVariableText = [
  {
    title: "custom-variable",
    id: 6,
  },
];
